import React, { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Pencil, Trash2, Shield, ShieldCheck, AlertCircle, Save, X, Search,
  Eye, PlusCircle, Pencil as PencilIcon, Trash, Check, Lock,
} from 'lucide-react'
import { toast } from 'react-toastify'
import {
  getProfiles, getModules, createProfile, updateProfile, deleteProfile, permissionsMapToArray,
} from '../../services/ProfileService'
import type { Profile, AppModuleInfo, PermissionsMap } from '../../types/@types/user'

// ─── Acciones ────────────────────────────────────────────────────────────────
const ACTIONS = ['view', 'create', 'edit', 'delete'] as const
type Action = typeof ACTIONS[number]

const ACTION_META: Record<Action, { label: string; icon: React.ReactNode; color: string }> = {
  view:   { label: 'Ver',     icon: <Eye size={13} />,        color: 'text-sky-600' },
  create: { label: 'Crear',   icon: <PlusCircle size={13} />, color: 'text-emerald-600' },
  edit:   { label: 'Editar',  icon: <PencilIcon size={13} />, color: 'text-amber-600' },
  delete: { label: 'Eliminar',icon: <Trash size={13} />,      color: 'text-rose-600' },
}

// ─── Agrupación de módulos por categoría ──────────────────────────────────────
const CATEGORY_GROUPS: { name: string; keys: string[] }[] = [
  { name: 'Operación',           keys: ['pos', 'kitchen', 'tables', 'floor_plan', 'order_history'] },
  { name: 'Caja',                keys: ['cash', 'cash_movements'] },
  { name: 'Catálogo',            keys: ['categories', 'products', 'flavors'] },
  { name: 'Inventario y Compras',keys: ['inventory', 'purchases', 'suppliers', 'expenses', 'shopping'] },
  { name: 'Clientes y Reportes', keys: ['customers', 'dashboard', 'reports', 'kpi'] },
  { name: 'Administración',      keys: ['users', 'settings', 'whatsapp'] },
]

function groupModules(modules: AppModuleInfo[]) {
  const used = new Set<string>()
  const groups = CATEGORY_GROUPS.map(g => ({
    name: g.name,
    mods: g.keys.map(k => modules.find(m => m.key === k)).filter(Boolean) as AppModuleInfo[],
  })).filter(g => g.mods.length > 0)
  groups.forEach(g => g.mods.forEach(m => used.add(m.key)))
  const others = modules.filter(m => !used.has(m.key))
  if (others.length) groups.push({ name: 'Otros', mods: others })
  return groups
}

// ─── Checkbox estilizado ──────────────────────────────────────────────────────
function PermBox({ checked, onChange, disabled, available }: {
  checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; available: boolean
}) {
  if (!available) return <span className="inline-block h-5 w-5 text-center text-text-muted/30">–</span>
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`inline-flex h-5 w-5 items-center justify-center rounded-md border transition
        ${checked
          ? 'border-primary bg-primary text-white shadow-sm'
          : 'border-border bg-surface hover:border-primary/50'}
        ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      aria-pressed={checked}
    >
      {checked && <Check size={13} strokeWidth={3} />}
    </button>
  )
}

// ─── Matriz de permisos agrupada ──────────────────────────────────────────────
interface PermMatrix {
  permissions: PermissionsMap
  modules: AppModuleInfo[]
  onChange: (module: string, action: Action, value: boolean) => void
  onBulk: (updater: (prev: PermissionsMap) => PermissionsMap) => void
  readOnly?: boolean
}

function PermissionMatrix({ permissions, modules, onChange, onBulk, readOnly }: PermMatrix) {
  const [search, setSearch] = useState('')

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase()
    const filtered = term ? modules.filter(m => m.label.toLowerCase().includes(term)) : modules
    return groupModules(filtered)
  }, [modules, search])

  const emptyPerm = { can_view: false, can_create: false, can_edit: false, can_delete: false }

  // ¿Está toda la columna marcada? (entre los módulos que soportan esa acción)
  const columnState = (action: Action) => {
    const applicable = modules.filter(m => m.availableActions.includes(action))
    const on = applicable.filter(m => permissions[m.key]?.[`can_${action}`]).length
    return on === 0 ? 'none' : on === applicable.length ? 'all' : 'some'
  }

  const toggleColumn = (action: Action) => {
    const target = columnState(action) !== 'all'
    onBulk(prev => {
      const next = { ...prev }
      modules.forEach(m => {
        if (m.availableActions.includes(action)) {
          next[m.key] = { ...(next[m.key] ?? emptyPerm), [`can_${action}`]: target }
        }
      })
      return next
    })
  }

  const toggleRow = (mod: AppModuleInfo) => {
    const cur = permissions[mod.key] ?? emptyPerm
    const allOn = mod.availableActions.every(a => cur[`can_${a}`])
    const target = !allOn
    onBulk(prev => {
      const p = { ...(prev[mod.key] ?? emptyPerm) }
      mod.availableActions.forEach(a => { (p as any)[`can_${a}`] = target })
      return { ...prev, [mod.key]: p }
    })
  }

  const toggleGroup = (mods: AppModuleInfo[], target: boolean) => {
    onBulk(prev => {
      const next = { ...prev }
      mods.forEach(m => {
        const p = { ...(next[m.key] ?? emptyPerm) }
        m.availableActions.forEach(a => { (p as any)[`can_${a}`] = target })
        next[m.key] = p
      })
      return next
    })
  }

  return (
    <div className="space-y-3">
      {/* Buscador */}
      <div className="relative">
        <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar módulo…"
          className="w-full rounded-lg border border-border bg-surface py-2 pl-9 pr-3 text-sm text-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-border">
        {/* Encabezado con toggles de columna */}
        <div className="grid grid-cols-[1fr_repeat(4,4.5rem)] items-center gap-1 border-b border-border bg-muted/50 px-4 py-2.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-text-muted">Módulo</span>
          {ACTIONS.map(a => {
            const st = columnState(a)
            return (
              <button
                key={a}
                type="button"
                disabled={readOnly}
                onClick={() => toggleColumn(a)}
                className="flex flex-col items-center gap-0.5 text-[11px] font-semibold text-text-muted transition hover:text-primary disabled:cursor-not-allowed"
                title={`Marcar/desmarcar todo: ${ACTION_META[a].label}`}
              >
                <span className={`flex items-center gap-1 ${ACTION_META[a].color}`}>
                  {ACTION_META[a].icon}{ACTION_META[a].label}
                </span>
                <span className={`h-1.5 w-6 rounded-full ${st === 'all' ? 'bg-primary' : st === 'some' ? 'bg-primary/40' : 'bg-border'}`} />
              </button>
            )
          })}
        </div>

        {/* Grupos */}
        <div className="divide-y divide-border">
          {groups.map(group => (
            <div key={group.name}>
              <div className="flex items-center justify-between bg-muted/25 px-4 py-1.5">
                <span className="text-[11px] font-bold uppercase tracking-wider text-text-muted">{group.name}</span>
                {!readOnly && (
                  <div className="flex gap-2 text-[11px]">
                    <button type="button" onClick={() => toggleGroup(group.mods, true)} className="font-medium text-primary hover:underline">Todo</button>
                    <span className="text-border">·</span>
                    <button type="button" onClick={() => toggleGroup(group.mods, false)} className="font-medium text-text-muted hover:underline">Nada</button>
                  </div>
                )}
              </div>
              {group.mods.map(mod => {
                const perm = permissions[mod.key] ?? emptyPerm
                const rowAll = mod.availableActions.every(a => perm[`can_${a}`])
                return (
                  <div key={mod.key} className="grid grid-cols-[1fr_repeat(4,4.5rem)] items-center gap-1 px-4 py-2 transition hover:bg-muted/20">
                    <button
                      type="button"
                      disabled={readOnly}
                      onClick={() => toggleRow(mod)}
                      className="flex items-center gap-2 text-left text-sm font-medium text-text disabled:cursor-default"
                      title="Marcar/desmarcar toda la fila"
                    >
                      <span className={`h-2 w-2 rounded-full ${rowAll ? 'bg-primary' : perm.can_view ? 'bg-primary/40' : 'bg-border'}`} />
                      {mod.label}
                    </button>
                    {ACTIONS.map(action => (
                      <div key={action} className="flex justify-center">
                        <PermBox
                          available={mod.availableActions.includes(action)}
                          checked={perm[`can_${action}`] ?? false}
                          disabled={readOnly}
                          onChange={v => onChange(mod.key, action, v)}
                        />
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
          ))}
          {groups.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-text-muted">No se encontraron módulos.</p>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Formulario de perfil ─────────────────────────────────────────────────────
interface FormState { name: string; description: string; permissions: PermissionsMap }

function buildEmptyPermissions(modules: AppModuleInfo[]): PermissionsMap {
  const map: PermissionsMap = {}
  modules.forEach(m => { map[m.key] = { can_view: false, can_create: false, can_edit: false, can_delete: false } })
  return map
}

function ProfileForm({ modules, initial, onSave, onCancel, loading }: {
  modules: AppModuleInfo[]; initial?: Profile; onSave: (d: FormState) => void; onCancel: () => void; loading: boolean
}) {
  const [form, setForm] = useState<FormState>(() => ({
    name: initial?.name ?? '',
    description: initial?.description ?? '',
    permissions: initial ? { ...buildEmptyPermissions(modules), ...initial.permissions } : buildEmptyPermissions(modules),
  }))

  const grantedCount = useMemo(
    () => modules.filter(m => form.permissions[m.key]?.can_view).length,
    [form.permissions, modules],
  )

  const handlePermChange = (module: string, action: Action, value: boolean) => {
    setForm(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: { ...(prev.permissions[module] ?? { can_view: false, can_create: false, can_edit: false, can_delete: false }), [`can_${action}`]: value },
      },
    }))
  }

  const applyPreset = (preset: 'all' | 'readonly' | 'clear') => {
    setForm(prev => {
      const next: PermissionsMap = {}
      modules.forEach(m => {
        const base = { can_view: false, can_create: false, can_edit: false, can_delete: false }
        if (preset === 'all') m.availableActions.forEach(a => { (base as any)[`can_${a}`] = true })
        else if (preset === 'readonly' && m.availableActions.includes('view')) base.can_view = true
        next[m.key] = base
      })
      return { ...prev, permissions: next }
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) { toast.error('El nombre es requerido'); return }
    onSave(form)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-text">Nombre del perfil</label>
          <input
            value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
            placeholder="Ej. Supervisor"
            disabled={initial?.isSystem}
          />
          {initial?.isSystem && (
            <p className="mt-1 flex items-center gap-1 text-xs text-text-muted"><Lock size={11} /> Perfil del sistema: solo se editan permisos</p>
          )}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-text">Descripción</label>
          <input
            value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Descripción opcional"
          />
        </div>
      </div>

      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-medium text-text">
            Permisos por módulo
            <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">{grantedCount} con acceso</span>
          </p>
          <div className="flex gap-2 text-xs">
            <button type="button" onClick={() => applyPreset('all')} className="rounded-lg border border-border px-2.5 py-1 font-medium text-text hover:bg-muted">Acceso total</button>
            <button type="button" onClick={() => applyPreset('readonly')} className="rounded-lg border border-border px-2.5 py-1 font-medium text-text hover:bg-muted">Solo lectura</button>
            <button type="button" onClick={() => applyPreset('clear')} className="rounded-lg border border-border px-2.5 py-1 font-medium text-text-muted hover:bg-muted">Limpiar</button>
          </div>
        </div>
        <PermissionMatrix
          permissions={form.permissions}
          modules={modules}
          onChange={handlePermChange}
          onBulk={(updater) => setForm(prev => ({ ...prev, permissions: updater(prev.permissions) }))}
        />
      </div>

      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-text hover:bg-muted">
          <X size={16} /> Cancelar
        </button>
        <button type="submit" disabled={loading} className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-fg hover:bg-primary/90 disabled:opacity-60">
          <Save size={16} /> {loading ? 'Guardando…' : 'Guardar perfil'}
        </button>
      </div>
    </form>
  )
}

// ─── Tarjeta de perfil ────────────────────────────────────────────────────────
function ProfileCard({ profile, modules, onEdit, onDelete }: {
  profile: Profile; modules: AppModuleInfo[]; onEdit: () => void; onDelete: () => void
}) {
  const accessible = modules.filter(m => profile.permissions[m.key]?.can_view)
  const total = modules.length

  return (
    <div className="flex flex-col rounded-2xl border border-border bg-surface p-5 shadow-sm transition hover:shadow-md">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${profile.isSystem ? 'bg-primary/10 text-primary' : 'bg-muted text-text-muted'}`}>
            {profile.isSystem ? <ShieldCheck size={20} /> : <Shield size={20} />}
          </div>
          <div>
            <p className="font-semibold text-text">{profile.name}</p>
            <p className="text-xs text-text-muted">
              {profile.isSystem ? 'Perfil del sistema · ' : ''}{accessible.length} de {total} módulos
            </p>
          </div>
        </div>
        <div className="flex gap-1">
          <button onClick={onEdit} className="rounded-lg p-1.5 text-text-muted hover:bg-muted hover:text-text" title="Editar"><Pencil size={15} /></button>
          {!profile.isSystem && (
            <button onClick={onDelete} className="rounded-lg p-1.5 text-text-muted hover:bg-red-50 hover:text-red-600" title="Eliminar"><Trash2 size={15} /></button>
          )}
        </div>
      </div>

      {profile.description && <p className="mb-3 text-xs text-text-muted">{profile.description}</p>}

      {/* Barra de cobertura */}
      <div className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${total ? (accessible.length / total) * 100 : 0}%` }} />
      </div>

      <div className="space-y-1">
        {accessible.length === 0 ? (
          <div className="flex items-center gap-1.5 text-xs text-text-muted"><AlertCircle size={13} /> Sin acceso a ningún módulo</div>
        ) : (
          accessible.slice(0, 8).map(m => (
            <div key={m.key} className="flex items-center justify-between text-xs">
              <span className="text-text-muted">{m.label}</span>
              <div className="flex gap-1">
                {ACTIONS.filter(a => m.availableActions.includes(a)).map(a => {
                  const on = profile.permissions[m.key]?.[`can_${a}`]
                  return (
                    <span key={a} title={ACTION_META[a].label}
                      className={`flex h-4 w-4 items-center justify-center rounded text-[9px] font-bold ${on ? 'bg-primary/15 text-primary' : 'bg-muted text-text-muted/30'}`}>
                      {ACTION_META[a].label[0]}
                    </span>
                  )
                })}
              </div>
            </div>
          ))
        )}
        {accessible.length > 8 && (
          <p className="pt-1 text-[11px] text-text-muted">+{accessible.length - 8} módulos más…</p>
        )}
      </div>
    </div>
  )
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function ProfilesPage() {
  const qc = useQueryClient()
  type EditingState = Profile | null | 'new'
  const [editing, setEditing] = useState<EditingState>(null)

  const { data: profiles = [], isLoading: loadingProfiles } = useQuery({ queryKey: ['profiles'], queryFn: getProfiles })
  const { data: modules = [], isLoading: loadingModules } = useQuery({ queryKey: ['profile-modules'], queryFn: getModules })

  const createMut = useMutation({
    mutationFn: (form: FormState) => createProfile({ name: form.name, description: form.description, permissions: permissionsMapToArray(form.permissions) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['profiles'] }); setEditing(null); toast.success('Perfil creado') },
    onError: (e: any) => toast.error(e.response?.data?.message ?? 'Error al crear perfil'),
  })
  const updateMut = useMutation({
    mutationFn: ({ id, form }: { id: number; form: FormState }) => updateProfile(id, { name: form.name, description: form.description, permissions: permissionsMapToArray(form.permissions) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['profiles'] }); setEditing(null); toast.success('Perfil actualizado') },
    onError: (e: any) => toast.error(e.response?.data?.message ?? 'Error al actualizar perfil'),
  })
  const deleteMut = useMutation({
    mutationFn: deleteProfile,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['profiles'] }); toast.success('Perfil eliminado') },
    onError: (e: any) => toast.error(e.response?.data?.message ?? 'No se puede eliminar'),
  })

  const handleSave = (form: FormState) => {
    if (editing === 'new') createMut.mutate(form)
    else if (editing !== null && typeof editing !== 'string') updateMut.mutate({ id: editing.id, form })
  }

  if (loadingProfiles || loadingModules) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text">Perfiles y Permisos</h1>
          <p className="mt-1 text-sm text-text-muted">Crea y gestiona perfiles con permisos personalizados por módulo</p>
        </div>
        {editing === null && (
          <button onClick={() => setEditing('new')} className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-fg hover:bg-primary/90">
            <Plus size={16} /> Nuevo perfil
          </button>
        )}
      </div>

      {/* Panel de edición */}
      {editing !== null && (
        <div className="rounded-2xl border border-border bg-surface p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <h2 className="text-lg font-semibold text-text">
              {editing === 'new' ? 'Crear nuevo perfil' : `Editar: ${(editing as Profile).name}`}
            </h2>
          </div>
          {/* Leyenda */}
          <div className="mb-4 flex flex-wrap gap-3 rounded-lg bg-muted/40 px-4 py-2 text-xs text-text-muted">
            <span className="font-semibold">Acciones:</span>
            {ACTIONS.map(a => (
              <span key={a} className={`flex items-center gap-1 ${ACTION_META[a].color}`}>{ACTION_META[a].icon}{ACTION_META[a].label}</span>
            ))}
            <span className="ml-auto hidden sm:inline">Tip: clic en el nombre del módulo marca/desmarca toda la fila</span>
          </div>
          <ProfileForm
            modules={modules}
            initial={editing === 'new' ? undefined : (editing as Profile)}
            onSave={handleSave}
            onCancel={() => setEditing(null)}
            loading={createMut.isPending || updateMut.isPending}
          />
        </div>
      )}

      {/* Tarjetas */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {profiles.map(profile => (
          <ProfileCard
            key={profile.id}
            profile={profile}
            modules={modules}
            onEdit={() => setEditing(profile)}
            onDelete={() => { if (confirm(`¿Eliminar el perfil "${profile.name}"?`)) deleteMut.mutate(profile.id) }}
          />
        ))}
      </div>
    </div>
  )
}
